import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationListComponent } from '@core/layout/navigation/notification/notification-list/notification-list.component';
import { ButtonDirective } from '@directives/button.directive';
import { OverlayDirective } from '@directives/overlay.directive';
import { BaseResponse, Pageable } from '@models/common.model';
import {
    ChangeStatusNotification,
    NotificationModel,
    NotificationSearchParam,
    NotificationStatusEnum,
} from '@models/notification.model';
import { AuthService } from '@services/auth.service';
import { DestroyService } from '@services/destroy.service';
import { DialogService } from '@services/dialog.service';
import { NotificationService } from '@services/notification.service';
import { ToastService } from '@services/toast.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, delay, filter, finalize, map, mergeMap, of, takeUntil } from 'rxjs';

@Component({
    selector: 'pn-notification',
    standalone: true,
    imports: [
        CommonModule,
        SvgIconComponent,
        ButtonDirective,
        OverlayDirective,
        NotificationListComponent,
    ],
    templateUrl: './notification.component.html',
    styleUrl: './notification.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationComponent implements OnInit {
    protected readonly _isMobile = this._device.isMobile();
    public loading = signal(false);
    public reloadLoading = signal(false);
    public notifications: NotificationModel[] = [];
    public hasUnreadNotification = signal(false);
    public allNotificationsLoaded = false;
    private readonly _session = toSignal(this._auth.session);
    private pageOptions: Pageable = {
        p: 1,
        ps: 20,
    };
    protected readonly _overlayPosition: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 33,
        },
    ];
    protected readonly _menuPosition: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 8,
        },
    ];

    constructor(
        private readonly _device: DeviceDetectorService,
        private readonly _notificationService: NotificationService,
        private readonly _dialogService: DialogService,
        private readonly _destroyService: DestroyService,
        private readonly _toastService: ToastService,
        private readonly _cd: ChangeDetectorRef,
        private readonly _auth: AuthService
    ) {}

    ngOnInit(): void {
        this._countNotification();
    }

    /**
     * Reload notifications
     *
     * @memberof NotificationComponent
     */
    public reloadNotifications() {
        this._countNotification();
        const pageable = {
            p: 1,
            ps: this.pageOptions.p * this.pageOptions.ps,
        };
        this.reloadLoading.set(true);
        this._loadNotificationsWithPageable(pageable, true);
    }

    /**
     * Count notification unread
     *
     * @private
     * @memberof NotificationComponent
     */
    private _countNotification() {
        this._notificationService
            .countNotificationUnRead()
            .pipe(
                catchError(() => of({ TotalItemUnRead: 0 })),
                map((res) => res?.TotalItemUnRead || 0),
                takeUntil(this._destroyService)
            )
            .subscribe((total) => {
                this.hasUnreadNotification.set(!!total);
            });
    }

    /**
     * Load notifications with pageable
     *
     * @private
     * @param {Pageable} pageOptions
     * @param {boolean} [reload]
     * @memberof NotificationComponent
     */
    private _loadNotificationsWithPageable(pageOptions: Pageable, reload?: boolean): void {
        this.loading.set(true);
        const searchParams: NotificationSearchParam = {
            ...pageOptions,
        };

        this._notificationService
            .searchNotificationByUser(searchParams)
            .pipe(
                catchError(() =>
                    of(
                        new BaseResponse<NotificationModel[]>({
                            Data: [],
                        })
                    )
                ),
                map((v) => v.Data),
                delay(500),
                finalize(() => {
                    this.loading.set(false);
                    this.reloadLoading.set(false);
                }),
                takeUntil(this._destroyService)
            )
            .subscribe((res) => {
                this.allNotificationsLoaded = !res?.length || res?.length < pageOptions.ps;
                if (!res?.length) {
                    return;
                }
                this.notifications = reload ? res : [...this.notifications, ...res];
                this._cd.detectChanges();
            });
    }

    /**
     * Handle open notification
     *
     * @param {boolean} event
     * @return {*}
     * @memberof NotificationComponent
     */
    public handleOpenNotification(event: boolean) {
        if (this.reloadLoading()) {
            return;
        }

        if (event) {
            this.allNotificationsLoaded = false;
            this.reloadNotifications();
        }
    }

    /**
     * Handle listener scroll to bottom
     *
     * @param {boolean} event
     * @return {*}
     * @memberof NotificationComponent
     */
    public listenerScrollToBottom(event: boolean) {
        if (this.loading() || !event) {
            return;
        }
        this.pageOptions.p++;
        this._loadNotificationsWithPageable(this.pageOptions);
    }

    /**
     * Handle read all notification
     *
     * @memberof NotificationComponent
     */
    public markAllAsRead() {
        const data = {
            title: $localize`:@@notification.labels.notification:Thông báo`,
            message: $localize`:@@notification.labels.confirmMarkAllAsRead:Đánh dấu các thông báo là đã đọc?`,
        };

        this._dialogService
            .confirm(data)
            .pipe(
                filter((result) => !!result),
                mergeMap(() => {
                    const unread = this.notifications.filter(
                        (v) => v.Status === NotificationStatusEnum.UnRead
                    );
                    const req: ChangeStatusNotification = {
                        ListNotificationId: unread.map((v) => v.NotificationId),
                        Status: NotificationStatusEnum.Read,
                    };

                    return this._notificationService.changeStatusNotification(req);
                }),
                takeUntil(this._destroyService)
            )
            .subscribe((res: BaseResponse) => {
                if (res?.ID > 0) {
                    this._toastService.success(
                        $localize`:@@notification.labels.mark-as-read:Đánh dấu đã đọc`,
                        res.Message
                    );
                    this.reloadNotifications();
                    return;
                }
                this._toastService.error(
                    $localize`:@@notification.labels.mark-as-read:Đánh dấu đã đọc`,
                    res?.Message ||
                        $localize`:@@notification.labels.mark-as-read-error:Đánh dấu không thành công`
                );
            });
    }

    /**
     * Handle close dialog
     *
     * @memberof NotificationComponent
     */
    public closeDialog() {
        this._dialogService.closeDialogById('notification-popup');
    }
}
